import { PropsOf } from "@emotion/react";
import styled from "@emotion/styled";
import { useIsDesktop } from "hooks/useDeviceType";
import { useDisplayOnClick } from "hooks/useDisplayOnClick";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { Token } from "store/global/TokenEnum";
import { addTokenToStore, sortBalancesInDescOrder } from "utils/tokenSelectorUtils";
import { ReactComponent as CloseIcon } from "../../img/butter_svgs/Close.svg";
import { TokenSelectorDropdown } from "./TokenSelectorDropdown";
import { compact, filter } from "lodash-es";
import { AnimatePresence } from "framer-motion";
import { AnimateChangeInHeight } from "components/general/AnimateChangeInHeight";
import { CounterParty } from "utils/tradeUtils";
import {
  getAutoCompleteString,
  getPlaceholder,
  getSortedTokensList,
} from "../../utils/TokenSelector/utils";

interface TokenSelectorProps {
  counterParty: CounterParty;
  isChangingToken: Token | undefined;
}

export const TokenSelector = forwardRef<HTMLDivElement, TokenSelectorProps>(
  ({ counterParty, isChangingToken }, ref) => {
    const [searchString, setSearchString] = useState("");
    const { balances, makerTokens, takerTokens, chain } = GlobalStore.useState(s => ({
      balances: s.balances,
      makerTokens: s.makerTokens,
      takerTokens: s.takerTokens,
      chain: s.network,
    }));
    const {
      isDisplayed: isDropdownDisplayed,
      elementToClickRef: inputRef,
      elementToDisplayRef: dropdownRef,
      forceOpen: openDropdown,
      forceClose: closeDropdown,
    } = useDisplayOnClick();
    const dropdownBoundingClientRect = dropdownRef.current?.getBoundingClientRect();
    const currentPartyTokens = counterParty === "taker" ? takerTokens : makerTokens;
    const isLastHighlightedWithKeyboard = useRef(false);

    const tokenList = useTokenListByChain();
    const sortedBalances = sortBalancesInDescOrder(balances, tokenList);
    const unselectedTokens = useMemo(() => {
      if (tokenList == null) return [];
      return compact(
        filter(tokenList, entry => {
          if (entry == null) return false;
          const inMakerTokens = new Set(makerTokens.map(t => t.token)).has(entry.ticker as Token);
          const inTakerTokens = new Set(takerTokens.map(t => t.token)).has(entry.ticker as Token);
          return !inMakerTokens && !inTakerTokens;
        })
      ).map(entry => entry?.ticker) as Array<Token>;
    }, [makerTokens, takerTokens, tokenList]);

    const tokensSortedByBalance = useMemo(
      () => getSortedTokensList(unselectedTokens, tokenList, sortedBalances, searchString),
      [unselectedTokens, searchString, sortedBalances, tokenList]
    );

    const [highlightedRowIdx, setHighlightedRowIdx] = useState<number | null>(null);
    const AutocompleteSuggestion = () => {
      if (tokensSortedByBalance == null) return null;
      return (
        <AutocompleteSuggestionContainer>
          {getAutoCompleteString(searchString, tokenList, tokensSortedByBalance)}
        </AutocompleteSuggestionContainer>
      );
    };

    const handleKeyPress = useCallback(
      (e: KeyboardEvent) => {
        if (tokensSortedByBalance == null) return;
        switch (e.key) {
          case "ArrowDown":
            isLastHighlightedWithKeyboard.current = true;
            if (highlightedRowIdx == null) {
              setHighlightedRowIdx(0);
            } else {
              const nextIdx =
                highlightedRowIdx === tokensSortedByBalance.length - 1 ? 0 : highlightedRowIdx + 1;
              setHighlightedRowIdx(nextIdx);
            }
            break;
          case "ArrowUp":
            isLastHighlightedWithKeyboard.current = true;
            if (highlightedRowIdx == null) {
              setHighlightedRowIdx(tokensSortedByBalance.length - 1);
            } else {
              const nextIdx =
                highlightedRowIdx === 0 ? tokensSortedByBalance.length - 1 : highlightedRowIdx - 1;
              setHighlightedRowIdx(nextIdx);
            }
            break;
          case "Enter": {
            if (highlightedRowIdx == null) return;
            const selectedToken = tokensSortedByBalance[highlightedRowIdx];
            if (selectedToken == null) return;
            addTokenToStore(counterParty, selectedToken);
            break;
          }
          case "Escape":
            closeDropdown();
            break;
          default:
            break;
        }
      },
      [closeDropdown, counterParty, highlightedRowIdx, tokensSortedByBalance]
    );

    useEffect(() => {
      if (isDropdownDisplayed) {
        window.addEventListener("keydown", handleKeyPress);
      } else {
        window.removeEventListener("keydown", handleKeyPress);
      }
      return () => {
        window.removeEventListener("keydown", handleKeyPress);
      };
    }, [handleKeyPress, isDropdownDisplayed]);

    useEffect(() => {
      if (currentPartyTokens.length === 0) return;
      inputRef.current?.focus();
      openDropdown();
    }, [currentPartyTokens.length, inputRef, openDropdown]);

    // Remove dropdown row highlights if search value changes
    useEffect(() => {
      isLastHighlightedWithKeyboard.current = false;
      if (searchString === "") return;
      setHighlightedRowIdx(0);
    }, [searchString]);

    useEffect(() => {
      setSearchString("");
    }, [chain]);

    return (
      <AnimatePresence>
        <Container ref={ref} initial={{ height: 0 }}>
          <Input
            ref={inputRef as React.MutableRefObject<HTMLInputElement>}
            placeholder={getPlaceholder(
              counterParty,
              isDropdownDisplayed,
              currentPartyTokens.length < 1
            )}
            value={searchString}
            onChange={e => setSearchString(e.target.value)}
            onBlur={() => setSearchString(s => s.trim())}
            onFocus={() => !isDropdownDisplayed && openDropdown()}
            onClick={() => !isDropdownDisplayed && openDropdown()}
            maxLength={100}
          />
          <AutocompleteSuggestion />
          {searchString && (
            <ClearButton
              onClick={() => {
                setSearchString("");
                inputRef.current?.focus();
              }}
            />
          )}
          {isDropdownDisplayed && !searchString && <CloseButton onClick={closeDropdown} />}

          <TokenSelectorDropdown
            ref={dropdownRef as React.MutableRefObject<HTMLDivElement>}
            isHidden={!isDropdownDisplayed}
            counterParty={counterParty}
            searchString={searchString}
            tokensToShow={tokensSortedByBalance}
            highlightedRowIdx={highlightedRowIdx}
            isLastHighlightedWithKeyboard={isLastHighlightedWithKeyboard}
            setHighlightedRowIdx={setHighlightedRowIdx}
            closeDropdown={closeDropdown}
            isChangingToken={isChangingToken}
            dropdownBoundingClientRect={dropdownBoundingClientRect}
            scrollToTop={() => {
              if (dropdownRef.current == null) return;
              dropdownRef.current.scrollTop = 0;
            }}
          />
        </Container>
      </AnimatePresence>
    );
  }
);

// Sub-components

const Container = styled(AnimateChangeInHeight)(() => {
  return { position: "relative", width: "100%", height: "100%" };
});

const Input = styled.input(({ theme }) => {
  const themeDevice = useIsDesktop() ? "Desktop" : "Mobile";
  return {
    position: "relative",
    zIndex: 2,
    width: "100%",
    height: "100%",
    background: "none",
    border: "none",
    padding: "15px 26px 14px 26px",
    font: theme[themeDevice].Title.Medium,
    lineHeight: 1.4,
    color: theme.font.primary.default,
    maxWidth: "90%",
    textOverflow: "ellipsis",
    "&:focus": {
      outline: "none",
    },

    "::placeholder": {
      color: theme.font.tertiary.default,
    },
  };
});

const AutocompleteSuggestionContainer = styled.div(({ theme }) => {
  const themeDevice = useIsDesktop() ? "Desktop" : "Mobile";
  return {
    position: "absolute",
    zIndex: 1,
    font: theme[themeDevice].Title.Medium,
    color: theme.font.tertiary.default,
    lineHeight: 1.4,
    top: 13,
    left: 26,
  };
});

const ClearButton = (props: PropsOf<typeof ClearButtonContainer>) => {
  return <ClearButtonContainer {...props}>Clear</ClearButtonContainer>;
};

const ClearButtonContainer = styled.button(({ theme }) => {
  return {
    position: "absolute",
    top: 18,
    right: 22,
    color: theme.font.tertiary.default,
    background: "none",
    border: "none",
    transition: "color linear 0.2s",
    zIndex: 20,

    "&:hover": {
      color: theme.font.tertiary.hover,
    },
  };
});

const CloseButton = (props: PropsOf<typeof CloseButtonContainer>) => {
  return (
    <CloseButtonContainer {...props}>
      <CloseIcon width={20} height={20} />
    </CloseButtonContainer>
  );
};

const CloseButtonContainer = styled.button(({ theme }) => {
  return {
    position: "absolute",
    top: 10,
    right: 22,
    color: theme.font.secondary.default,
    background: theme.button.tertiary.surface.default,
    border: "none",
    borderRadius: "50%",
    width: 32,
    height: 32,
    zIndex: 20,
    transition: "color linear 0.2s, background linear 0.2s",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    "&:hover": {
      color: theme.font.tertiary.hover,
      background: theme.button.tertiary.surface.hover,
    },
  };
});
